import { Seo } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { Reveal, Eyebrow } from "../components/motion";
import { QuoteCTA } from "../components/QuoteCTA";

const MODEL = [
  { t: "Professionally managed", d: "Every account is overseen by Shine Solutions management — from scoping and scheduling to communication and service standards." },
  { t: "Qualified cleaning teams", d: "Cleaning is carried out by qualified cleaning teams and partner companies selected and coordinated by Shine Solutions, working to your written scope." },
  { t: "Long-term relationships", d: "We focus on recurring commercial contracts, so our approach is built around consistency over months and years, not one-off visits." },
];

const PROCESS = [
  { t: "Documented cleaning scope", d: "A written, area-by-area scope agreed with you before service begins, so expectations are clear on both sides." },
  { t: "Recurring service schedule", d: "Visit days, frequency and service windows set out in your proposal and planned as a recurring schedule." },
  { t: "Accountable point of contact", d: "One named contact for your account who handles questions, changes and concerns." },
  { t: "Quality follow-up", d: "Periodic check-ins against your scope, with follow-up when something needs attention." },
];

export default function About() {
  return (
    <>
      <Seo title="About Shine Solutions | West Valley Commercial Cleaning" description="Shine Solutions is a professionally managed commercial cleaning company serving Peoria, Glendale and Surprise, AZ, focused on recurring cleaning contracts." path="/about" />
      <PageHero kicker="About our company" lines={["Professionally", "managed. Reliably", "delivered."]} intro="Shine Solutions provides professionally managed commercial cleaning for West Valley businesses that want a dependable, long-term partner — consistent scheduling, clear service standards and responsive communication." image="/images/facade.jpg" alt="Professional cleaning glass facade of a commercial building" />
      <section className="bg-white py-28 md:py-40" data-testid="about-model">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <Reveal><Eyebrow>Who we are</Eyebrow></Reveal>
          <Reveal delay={0.08}><h2 className="mt-6 max-w-3xl font-display text-4xl font-semibold leading-[1] tracking-tight text-ink md:text-6xl">A cleaning partner you don't have to manage.</h2></Reveal>
          <div className="mt-20 grid gap-12 md:grid-cols-3">
            {MODEL.map((m, i) => (
              <Reveal key={m.t} delay={0.08 * i} className="border-t-2 border-ink pt-8">
                <span className="font-mono text-xs text-emerald-700">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-6 font-display text-2xl font-semibold text-ink">{m.t}</h3>
                <p className="mt-4 text-base leading-relaxed text-slate-600">{m.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <section className="bg-porcelain py-28 md:py-40" data-testid="about-standards">
        <div className="mx-auto grid max-w-[1400px] gap-16 px-5 md:px-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="clip-cut-alt h-[420px] overflow-hidden md:h-[540px]"><img src="/images/lobby.jpg" alt="Clean modern commercial building lobby" loading="lazy" className="h-full w-full object-cover" /></div>
          </Reveal>
          <div className="lg:col-span-7 lg:pt-8">
            <Reveal><Eyebrow>Our management approach</Eyebrow></Reveal>
            <Reveal delay={0.08}><h2 className="mt-6 font-display text-4xl font-semibold leading-[1] tracking-tight text-ink md:text-5xl">How we plan to manage every account.</h2></Reveal>
            <ul className="mt-12 divide-y divide-slate-300 border-y border-slate-300" data-testid="about-management-process">
              {PROCESS.map((s, i) => (
                <Reveal as="li" key={s.t} delay={0.05 * i} className="grid grid-cols-[2.5rem_1fr] gap-4 py-6">
                  <span className="font-mono text-xs leading-7 text-emerald-700">{String(i + 1).padStart(2, "0")}</span>
                  <div><p className="font-display text-xl font-semibold text-ink md:text-2xl">{s.t}</p><p className="mt-2 text-base text-slate-600">{s.d}</p></div>
                </Reveal>
              ))}
            </ul>
            <Reveal delay={0.2}><p className="mt-6 text-sm text-slate-500">Specific service commitments for your facility are confirmed in writing in your proposal.</p></Reveal>
          </div>
        </div>
      </section>
      <QuoteCTA title="Work with an accountable partner." />
    </>
  );
}
